import {
  Box,
  Dialog,
  Stack,
  Typography,
  Chip,
  Avatar,
} from '@mui/material';
import { useMemo, useState, useEffect } from 'react';
import { useTheme } from '@emotion/react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { BackArrow } from '../../assets';
import { Wrapper } from '../wrapper';
import { CustomTable } from '../table';
import { DataColumns } from '../view-logs/constant';
import { Modal } from '../modal';
import { getLogBySequenceId, viewLogs, dashboardRecentActiviy } from '../../services';
import ViewLogDialogBox from '../../pages/dashboard/viewLogDialogBox';
import { formatDateToDMY } from '../../utils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  CheckCircle,
  CreditCard,
  Mail,
  VpnKey,
} from '@mui/icons-material';
dayjs.extend(relativeTime);

export const DetailedLogs = () => {
  const location = useLocation();
  const { date, selectedApiId, selectedCategory, showRecentActivities } = location.state || {};

  const theme = useTheme();
  const { user } = useSelector((state) => state.auth);
  const jwtToken = user?.jwt;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [logData, setLogData] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [sequenceLogData, setSequenceLogData] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (showRecentActivities) {
        try {
          const res = await dashboardRecentActiviy(jwtToken);
          if (res?.status === 200) {
            setRecentActivities(res?.data?.data || []);
          }
        } catch (error) {
          console.error('Error fetching recent activities:', error);
        }
      } else {
        try {
          const res = await viewLogs({
            filterDate: date,
            token: jwtToken,
            id: selectedCategory === 'all' ? null : selectedApiId,
          });
          setLogData(res?.data || []);
        } catch (error) {
          console.error('Error fetching logs:', error);
        }
      }
    };

    if (date || showRecentActivities) {
      fetchData();
    }
  }, [date, jwtToken, selectedApiId, selectedCategory, showRecentActivities]);

  const handleModal = () => {
    setIsDialogOpen(false);
  };

  const handleSequenceClick = async (sequenceId) => {
    setSelectedSequence(sequenceId);
    setOpen(true);
    try {
      const data = await getLogBySequenceId(sequenceId);
      setSequenceLogData(data);
    } catch (error) {
      console.error('Error fetching sequence log:', error);
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'api_call':
        return <CheckCircle sx={{ fontSize: 16, color: '#10b981' }} />;
      case 'credit_purchase':
        return <CreditCard sx={{ fontSize: 16, color: '#f59e0b' }} />;
      case 'support_mail':
        return <Mail sx={{ fontSize: 16, color: '#3b82f6' }} />;
      case 'key_generation':
        return <VpnKey sx={{ fontSize: 16, color: '#8b5cf6' }} />;
      default:
        return <CheckCircle sx={{ fontSize: 16, color: '#64748b' }} />;
    }
  };

  const getActivityStatus = (type, metadata) => {
    if (type === 'api_call') {
      const status = metadata?.statusCode === 200 ? 'Success' : 'Failed';
      const color = metadata?.statusCode === 200 ? '#10b981' : '#ef4444';
      return <Chip label={status} size="small" sx={{ backgroundColor: `${color}15`, color }} />;
    }
    return <Chip label="Success" size="small" sx={{ backgroundColor: '#10b98115', color: '#10b981' }} />;
  };
 

  const convertRecentActivitiesData = (data) => {
    return data?.map((item, index) => ({
      key: index.toString(),
      type: (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center', // ✅ centers horizontally
            gap: 1,
          }}
        >
          {getActivityIcon(item.type)}
          <Typography
            sx={{
              textTransform: 'capitalize',
              fontSize: '14px',
              fontFamily: 'Inter',
              fontWeight: 400,
            }}
          >
            {item.type.replace('_', ' ')}
          </Typography>
        </Box>
      ),
      description: (
        <Typography sx={{ fontSize: '14px', fontFamily: 'Inter', fontWeight: 400 }}>
          {item.description}
        </Typography>
      ),
      timestamp: (
        <Typography sx={{ fontSize: '14px', fontFamily: 'Inter', fontWeight: 400 }}>
          {dayjs(item.timestamp).format('MMM DD, YYYY HH:mm:ss')}
        </Typography>
      ),
      status: getActivityStatus(item.type, item.metadata),
      metadata: (
        <Typography sx={{ fontSize: '14px', fontFamily: 'Inter', fontWeight: 400 }}>
          {item.metadata?.apiName || item.metadata?.keyPrefix || item.metadata?.amount || '-'}
        </Typography>
      ),
    }));
  };
  
  const convertData = (data) => {
    if (data.message === 'No data found for the specified date.') {
      return null;
    }
    return data?.map((item, index) => ({
      key: index.toString(),
      apiName: item?.apiName,
      requestTime: formatDateToDMY(item?.requestTime, true),
      responseTime: item?.responseTime,
      httpStatus: item?.httpStatus,
      status: (
        <Box
          component="span"
          color={item.status === 'Fetched' ? 'green' : '#FF4A4A'}
        >
          {item.status}
        </Box>
      ),
      sequenceID: (
        <Box
          component="span"
        >
          {item?.requestId}
        </Box>
      ),
      creditUtilized: item?.totalConsumedCredit,
    }));
  };

  const data = useMemo(() => {
    if (showRecentActivities) {
      return convertRecentActivitiesData(recentActivities);
    }
    return convertData(logData);
  }, [logData, recentActivities, showRecentActivities]);

  const activityColumns = [
    { title: 'Type', dataIndex: 'type', key: 'type' },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    { title: 'Timestamp', dataIndex: 'timestamp', key: 'timestamp' },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    { title: 'Details', dataIndex: 'metadata', key: 'metadata' },
  ];

  const REQUEST_DETAILS = useMemo(
    () => [
      {
        label: 'API',
        value: 'Aadhaar Masking',
      },
      {
        label: 'ENV',
        value: 'Production',
      },
      {
        label: 'HTTP Status',
        value: '200',
      },
      {
        label: 'Internal Status',
        value: '...',
      },
      {
        label: 'Internal status description',
        value: 'Data fetched successfully',
      },
      {
        label: 'Request ID',
        value: '...',
      },
      {
        label: 'Request time',
        value: 'Jan 17,2024',
      },
      {
        label: 'Response time',
        value: 'Jan 17,2024',
      },
    ],
    [],
  );

  return (
    <Box>
      <Wrapper sx={{ paddingTop: theme.spacing(8) }}>
        <Stack direction="row" sx={{ pt: 3, pb: 3 }} spacing={2}>
          <Box
            onClick={() => showRecentActivities ? navigate("/") : navigate("/view-logs")}
            component="img"
            sx={{ cursor: "pointer" }}
            src={BackArrow}
          />
          
          <Typography
            sx={{ color: theme.palette.textColor.primary }}
            variant="h4"
          >
            Recent Activities
          </Typography>
        </Stack>

        
        <CustomTable columns={showRecentActivities ? activityColumns : DataColumns} data={data} />
      </Wrapper>

      <Dialog
        open={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
        }}
        maxWidth="lg"
      >
        <Modal REQUEST_DETAILS={REQUEST_DETAILS} handleModal={handleModal} />
      </Dialog>

      <ViewLogDialogBox
        sequenceLogData={sequenceLogData}
        open={open}
        setOpen={setOpen}
        sequenceId={selectedSequence}
      />
    </Box>
  );
};